import { Headers } from 'bernie-graphql';
import { AnalyticsStore, ContextStore } from 'bernie-plugin-mobx';
import { SerializedData } from 'bernie-core';
import { getBexApiContext } from 'bernie-context';
import { action, observable, runInAction } from 'mobx';
import { getErrorString, SystemEvent, Logger, NOOP_LOGGER } from 'bernie-logger';
import { DetailCriteriaStore } from 'stores';

import {
  CarAccessorySelectionState,
  CarDetail,
  ContextInput,
  RewardsPointSelectionEnum
} from 'common/__generated__/api';
import { STORE_EVENTS } from './store-event';
import { CarsStore } from './cars-store';
import analyticsUtil from 'utils/analytics-util';
import { ErrorType } from 'components/common/ErrorType';

export class CarDetailStore extends CarsStore {
  private context: ContextInput;
  private headers: Headers;
  private API_TIMEOUT:number = 30000
  private RETRY_COUNT:number = 1

  @observable
  public carDetail: CarDetail.CarDetail;

  @observable
  public vehicle: CarDetail.Vehicle;

  @observable
  public vendor: CarDetail.Vendor;

  @observable
  public traceId: string;

  @observable
  public isLoading: boolean = true;

  @observable
  public errorInFetchingResult: boolean = false;

  @observable
  public detailOfferPiid: string;

  @observable
  public srpQueryParams: string;

  @observable
  public timeOutCompleted: boolean;

  @observable
  public piidUpdated: boolean = false;

  public clickedSearchOfferPiid: string;

  public resolvedFromServer: boolean;

  @observable
  public inRecoverableErrorState: boolean = false;

  @observable
  public criteriaChangeError: ErrorType = null;

  public constructor(bexConfigs, logger: Logger = NOOP_LOGGER, appconfig: SerializedData, param) {
    super(bexConfigs, logger, appconfig, param);
    this.logger = logger;
  }

  public hydrate(data: SerializedData) {
    super.hydrate(data);
    this.context = { ...data.context };
    this.carDetail = { ...data.carDetail };
    this.resolvedFromServer = this.carDetail ? true : false;
    this.detailOfferPiid = data.detailOfferPiid;
  }

  public setContext(context: ContextStore) {
    this.context = getBexApiContext(context);
  }

  public failureHandler(
    variable: CarDetail.Variables,
    error: any,
    event: SystemEvent,
    analytics: AnalyticsStore,
    pageId: string,
    deviceId: string
  ) {
    this.logger.logEvent(event, variable, getErrorString(error));
    analyticsUtil.publicCustomClientSideAnalyticsPayload(pageId, analytics, deviceId, event.name);
  }

  // called from SRP page to reset available model to remove page Flicker
  public resetCarDetail(receivedNewPiid: string, search: string) {
    this.carDetail = null;
    this.vehicle = null;
    this.vendor = null;
    // TODO remove clickedSearchOfferPiid as this is not used nay where. Components calling this method are using detailscontext to pass piid
    // TODO components using this method - offer-card-mobile, offer-card-desktop, drop-off-loading-view, drop-off-submit-button
    this.clickedSearchOfferPiid = receivedNewPiid;
    this.resolvedFromServer = false;
    this.srpQueryParams = search;
    this.criteriaChangeError = null;
    this.traceId = null;
  }

  @action
  public getCarDetail(
    detailCriteriaStore: DetailCriteriaStore,
    context: ContextStore,
    pageId: string,
    isServer: boolean = false,
    analytics: AnalyticsStore,
    domainServiceRedirect: boolean
  ): Promise<any> {
    // TODO: to move it into the parent-store level to overcome replication
    this.isLoading = !detailCriteriaStore.loadingCriteriaChange;
    const detailVariables: CarDetail.Variables = detailCriteriaStore.detailVariables;

    this.bexApiSource.headers = this.addDomainRedirectHeader(this.bexApiSource.headers, domainServiceRedirect);

    return this.bexApiSource.operations
      .carDetail(detailVariables, {
        pageId
      })
      .then(response => {
        runInAction('Car Detail Results', () => {
          // response = require('../../.offline/mock-detail-response.json');
          const carDetail = response?.data?.carDetail;
          const trace = response?.extensions?.trace;

          if (carDetail) {
            if (!isServer) {
              this.startResultsExpirationTimer(() => (this.timeOutCompleted = true));
            }
            this.carDetail = carDetail;
            this.vehicle = carDetail.vehicle;
            this.vendor = carDetail.vendor;
            this.detailOfferPiid = carDetail.carOfferToken;
            this.traceId = trace['Trace-ID'];
            this.errorInFetchingResult = false;
            this.criteriaChangeError = null;
          }

          const errors = response?.errors;
          if (errors && errors.length > 0) {
            //If detail failure is triggered by accessory selection
            if (detailCriteriaStore.loadingCriteriaChange) {
              this.inRecoverableErrorState = true;

              if (detailCriteriaStore.extraSelectionChanging) {
                this.criteriaChangeError = ErrorType.InfoSiteExtrasError;
                // Reverting added/removed accessory
                if (detailCriteriaStore.removedAccessory) {
                  this.carDetail.availableAccessories.accessories.find(
                    accessory => accessory.id === detailCriteriaStore.removedAccessory
                  ).selectionState = CarAccessorySelectionState.SELECTED;
                } else {
                  const addedAccessory = detailCriteriaStore.getAccessoriesList().pop();
                  this.carDetail.availableAccessories.accessories.find(
                    accessory => accessory.id === addedAccessory
                  ).selectionState = CarAccessorySelectionState.NOT_SELECTED;
                }
              }
              if (detailCriteriaStore.pointsSelectionChanging) {
                this.criteriaChangeError =
                  detailCriteriaStore.rewardPointsSelection === RewardsPointSelectionEnum.DO_NOT_APPLY_REWARD_POINTS
                    ? ErrorType.RemoveRewardsError
                    : ErrorType.ApplyRewardsError;
                //Revert points selection
                detailCriteriaStore.rewardPointsSelection =
                  detailCriteriaStore.rewardPointsSelection === RewardsPointSelectionEnum.DO_NOT_APPLY_REWARD_POINTS
                    ? RewardsPointSelectionEnum.APPLY_REWARD_POINTS
                    : RewardsPointSelectionEnum.DO_NOT_APPLY_REWARD_POINTS;
              }
              if (detailCriteriaStore.rentalProtectionChanging) {
                this.criteriaChangeError = ErrorType.SABSelectionError;
                const selectedIndex = this.carDetail.rentalProtectionInfo.rentalProtectionCard.findIndex(
                  item => item.selected === true
                );
                if (selectedIndex !== detailCriteriaStore.selectedSABCardIndex) {
                  detailCriteriaStore.selectedSABCardIndex = selectedIndex;
                }
              }
            } else {
              //If detail failure is not triggered by accessory selection, redirect customers to CKO page
              analyticsUtil.publishAnalyticsPayload(response, analytics);
              window.location.replace(
                `/carsearch/book?piid=${detailVariables.carDetailContext.carOfferToken}&searchUrl=${encodeURIComponent(
                  detailCriteriaStore.detailQuery.searchUrl
                )}`
              );
            }
          }
          // removedAccessory is only needed when downstream call failed
          detailCriteriaStore.removedAccessory = null;
          if (!detailCriteriaStore.loadingCriteriaChange) {
            analyticsUtil.publishAnalyticsPayload(response, analytics);
          }
        });
      })
      .catch(error => {
        runInAction('Send user to CKO page on error', () => {
          this.carDetail = null;
          this.errorInFetchingResult = true;
          this.failureHandler(
            detailVariables,
            error,
            STORE_EVENTS.DETAIL_AVAILABILITY_FETCH_FAILED,
            analytics,
            pageId,
            context.deviceId
          );
          window.location.replace(
            `/carsearch/book?piid=${detailVariables.carDetailContext.carOfferToken}&searchUrl=${encodeURIComponent(
              detailCriteriaStore.detailQuery.searchUrl
            )}`
          );
        });
      })
      .finally(() => {
        detailCriteriaStore.pointsSelectionChanging = false;
        detailCriteriaStore.extraSelectionChanging = false;
        detailCriteriaStore.rentalProtectionChanging = false;
        this.isLoading = false;
        detailCriteriaStore.loadingCriteriaChange = false;
        detailCriteriaStore.optionToken = null;
        detailCriteriaStore.optionTokenState = null;
      });
  }
}
